import { OrderStatus } from "presentation/stores/order-store";

/** 순서를 바꿀 형제들의 부모. 옵션은 상품, 속성은 옵션, 값은 속성 아래에서만 순서를 바꾼다 */
export type OptionReorderScope =
  | { level: "option" }
  | { level: "attribute"; optionId: number }
  | { level: "value"; optionId: number; attributeId: number };

export const NEW_API_ENDPOINTS = {
  routerController: {
    PUT: (routeId: number): string => `/${routeId}`,
  },

  userController: {
    GET: "/api/user" as const,
    PUT: "/api/user" as const,
  },

  companyController: {
    GET: {
      byId: (companyId: number): string => `/api/company/${companyId}`,
      users: (companyId: number): string => `/api/company/${companyId}/user`,
      realtimeGraph: (companyId: number): string =>
        `/api/company/${companyId}/realtime-graph`,
      adminInfo: (companyId: number): string =>
        `/api/company/${companyId}/admin`,
    },
    PUT: {
      update: (companyId: number): string => `/api/company/${companyId}`,
      updateUserRole: (companyId: number): string =>
        `/api/company/${companyId}/user/role`,
      request: "/api/company/request" as const,
    },
    DELETE: {
      /** 회사 소속 쇼핑몰 회원 탈퇴 처리 (COMPANY_ADMIN 전용) */
      withdrawUser: (companyId: number, userId: number): string =>
        `/api/company/${companyId}/user/${userId}`,
    },
  },

  adminController: {
    PUT: {
      cancelRequest: "/api/admin/company/request/cancel" as const,
      updateCompanyInfo: (companyId: number): string =>
        `/api/admin/company/${companyId}`,
    },
    POST: {
      approveRequest: "/api/admin/company/request/approve" as const,
    },
    GET: {
      listCompaniesWithStatus: "/api/admin/company/with-status" as const,
      actionRequiredCompanies: "/api/admin/company/action-required" as const,
      byId: (companyId: number): string => `/api/admin/company/${companyId}`,
      companyOverview: (companyId: number): string =>
        `/api/admin/company/${companyId}/overview`,
      companyDataReportSummary: (companyId: number): string =>
        `/api/admin/company/${companyId}/data-report/summary`,
      companyDataReportLogs: (companyId: number): string =>
        `/api/admin/company/${companyId}/data-report/logs`,
      companyEquipmentStatus: (companyId: number): string =>
        `/api/admin/company/${companyId}/equipment-status`,
      requests: "/api/admin/company/request" as const,
      // 어드민 "사용자 관리" 화면 (Users.tsx)
      managerUsers: "/api/admin/user/managers" as const,
      generalUsers: "/api/admin/user/general" as const,
      generalUserSummary: "/api/admin/user/general/summary" as const,
      withdrawalPendingUsers: "/api/admin/user/withdrawal-pending" as const,
    },
  },

  machineController: {
    POST: (routerId: number): string => `/api/router/${routerId}/machine`,
  },

  machineProductController: {
    POST: "/api/machine-product/create" as const,
    GET: "/api/machine-product" as const,
  },

  adminMachineProductController: {
    GET_ALL: "/api/admin/machine-product" as const,
    GET_BY_ID: (id: number): string => `/api/admin/machine-product/${id}`,
    CREATE: "/api/admin/machine-product" as const,
    UPDATE: (id: number): string => `/api/admin/machine-product/${id}`,
    DELETE: (id: number): string => `/api/admin/machine-product/${id}`,
    GET_ALL_UNREPORTED: "/api/admin/machine-product/unreported" as const,
    GET_UNREPORTED_BY_ID: (id: number): string =>
      `/api/admin/machine-product/unreported/${id}`,
    DELETE_UNREPORTED: (id: number): string =>
      `/api/admin/machine-product/unreported/${id}`,
  },

  authenticationController: {
    POST: {
      verifyPassword: "/api/auth/verifyPassword" as const,
      social: "/api/auth/social" as const,
      signup: "/api/auth/signup" as const,
      reissue: "/api/auth/reissue" as const,
      login: "/api/auth/login" as const,
      /** 여러 회사에 속한 계정의 워크스페이스 선택 → 토큰 */
      workspace: "/api/auth/workspace" as const,
    },
  },

  /** 구성원(회사 소유자·관리자)과 관리자 초대 */
  companyStaffController: {
    GET_STAFF: (companyId: number): string => `/api/company/${companyId}/staff`,
    GET_INVITATIONS: (companyId: number): string => `/api/company/${companyId}/invitations`,
    POST_INVITATION: (companyId: number): string => `/api/company/${companyId}/invitations`,
    POST_RESEND: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invitations/${id}/resend`,
    DELETE_INVITATION: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invitations/${id}`,
  },

  /** 초대 수락 (공개, 로그인 불필요) */
  invitationController: {
    GET: (token: string): string => `/api/invitations/${encodeURIComponent(token)}`,
    POST_ACCEPT_PASSWORD: (token: string): string =>
      `/api/invitations/${encodeURIComponent(token)}/accept/password`,
    POST_ACCEPT_SOCIAL: (token: string): string =>
      `/api/invitations/${encodeURIComponent(token)}/accept/social`,
    POST_ACCEPT_SIGNUP: (token: string): string =>
      `/api/invitations/${encodeURIComponent(token)}/accept/signup`,
  },

  adminRouterController: {
    GET: (companyId: number): string =>
      `/api/admin/company/${companyId}/router`,
    POST: (companyId: number): string =>
      `/api/admin/company/${companyId}/router`,
  },

  addressController: {
    POST: "/api/address" as const,
  },

  machineSuppliesController: {
    GET: {
      list: (machineId: number): string =>
        `/api/machines/${machineId}/supplies`,
      byId: (machineId: number, suppliesId: number): string =>
        `/api/machines/${machineId}/supplies/${suppliesId}`,
    },
  },

  companyRouterController: {
    GET: (companyId: number): string => `/api/company/${companyId}/router`,
  },

  companyMachineController: {
    GET: (companyId: number): string => `/api/company/${companyId}/machine`,
  },

  companyRouterMachinesController: {
    GET: (companyId: number): string =>
      `/api/company/${companyId}/machine/by-router`,
  },

  companyOptionController: {
    // Option CRUD
    GET: (companyId: number): string => `/api/company/${companyId}/options`,
    GET_DETAIL: (companyId: number, optionId: number): string =>
      `/api/company/${companyId}/options/${optionId}`,
    POST: (companyId: number): string => `/api/company/${companyId}/options`,
    PUT: (companyId: number, optionId: number): string =>
      `/api/company/${companyId}/options/${optionId}`,
    DELETE: (companyId: number, optionId: number): string =>
      `/api/company/${companyId}/options/${optionId}`,

    // Attribute CRUD
    POST_ATTRIBUTE: (companyId: number, optionId: number): string =>
      `/api/company/${companyId}/options/${optionId}/attributes`,
    PUT_ATTRIBUTE: (
      companyId: number,
      optionId: number,
      attributeId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/attributes/${attributeId}`,
    DELETE_ATTRIBUTE: (
      companyId: number,
      optionId: number,
      attributeId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/attributes/${attributeId}`,

    // Value CRUD
    POST_VALUE: (
      companyId: number,
      optionId: number,
      attributeId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/attributes/${attributeId}/values`,
    PUT_VALUE: (
      companyId: number,
      optionId: number,
      attributeId: number,
      valueId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/attributes/${attributeId}/values/${valueId}`,
    DELETE_VALUE: (
      companyId: number,
      optionId: number,
      attributeId: number,
      valueId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/attributes/${attributeId}/values/${valueId}`,

    // Combination CRUD
    POST_COMBINATION: (companyId: number, optionId: number): string =>
      `/api/company/${companyId}/options/${optionId}/combinations`,
    PUT_COMBINATION: (
      companyId: number,
      optionId: number,
      combinationId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}`,
    DELETE_COMBINATION: (
      companyId: number,
      optionId: number,
      combinationId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}`,

    // CombinationKey CRUD
    POST_COMBINATION_KEY: (
      companyId: number,
      optionId: number,
      combinationId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys`,
    POST_COMBINATION_KEY_BULK: (
      companyId: number,
      optionId: number,
      combinationId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys/bulk`,
    PUT_COMBINATION_KEY: (
      companyId: number,
      optionId: number,
      combinationId: number,
      keyId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys/${keyId}`,
    DELETE_COMBINATION_KEY: (
      companyId: number,
      optionId: number,
      combinationId: number,
      keyId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys/${keyId}`,

    // PriceTier CRUD
    POST_PRICE_TIER: (
      companyId: number,
      optionId: number,
      combinationId: number,
      keyId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys/${keyId}/tiers`,
    POST_PRICE_TIER_BULK: (
      companyId: number,
      optionId: number,
      combinationId: number,
      keyId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys/${keyId}/tiers/bulk`,
    PUT_PRICE_TIER_BULK: (
      companyId: number,
      optionId: number,
      combinationId: number,
      keyId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys/${keyId}/tiers/bulk`,
    PUT_PRICE_TIER: (
      companyId: number,
      optionId: number,
      combinationId: number,
      keyId: number,
      tierId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys/${keyId}/tiers/${tierId}`,
    DELETE_PRICE_TIER: (
      companyId: number,
      optionId: number,
      combinationId: number,
      keyId: number,
      tierId: number,
    ): string =>
      `/api/company/${companyId}/options/${optionId}/combinations/${combinationId}/keys/${keyId}/tiers/${tierId}`,
  },

  companyProductController: {
    // 상품 목록 조회 (카테고리별)
    GET: (companyId: number): string => `/api/company/${companyId}/products`,
    // 상품 상세 조회
    GET_DETAIL: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}`,
    // 상품 생성
    POST: (companyId: number): string => `/api/company/${companyId}/products`,
    // 상품 수정
    PUT: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}`,
    // 상품 삭제
    DELETE: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}`,
    // 상품 옵션 연결
    LINK_OPTIONS: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/options`,
    // 상품 옵션 연결 해제
    UNLINK_OPTION: (
      companyId: number,
      productId: number,
      productOptionId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${productOptionId}`,
    // 개별 상품 옵션 삭제
    DELETE_OPTION: (
      companyId: number,
      productId: number,
      productOptionId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${productOptionId}`,
    DETAIL_PAGES: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/detail-pages`,
    DETAIL_PAGES_ORDER: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/detail-pages/order`,
    DETAIL_PAGES_INSERT: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/detail-pages/insert`,
    DETAIL_PAGES_DELETE: (
      companyId: number,
      productId: number,
      detailPageId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/detail-pages/${detailPageId}`,
  },

  // 카테고리 관리 API (별도 컨트롤러)
  companyCategoryController: {
    // 카테고리 목록 조회
    GET: (companyId: number): string => `/api/company/${companyId}/categories`,
    // 카테고리 상세 조회
    GET_DETAIL: (companyId: number, categoryId: number): string =>
      `/api/company/${companyId}/categories/${categoryId}`,
    // 카테고리 생성
    POST: (companyId: number): string => `/api/company/${companyId}/categories`,
    // 카테고리 수정
    PUT: (companyId: number, categoryId: number): string =>
      `/api/company/${companyId}/categories/${categoryId}`,
    // 카테고리 삭제
    DELETE: (companyId: number, categoryId: number): string =>
      `/api/company/${companyId}/categories/${categoryId}`,
    // 카테고리 순서 대량 변경
    BULK_UPDATE_ORDER: (companyId: number): string =>
      `/api/company/${companyId}/categories/order/bulk`,
  },

  companyBankTransferController: {
    GET: (companyId: number): string =>
      `/api/company/${companyId}/bank-transfer`,
    POST: (companyId: number): string =>
      `/api/company/${companyId}/bank-transfer`,
    PUT: (companyId: number, bankTransferId: number): string =>
      `/api/company/${companyId}/bank-transfer/${bankTransferId}`,
    DELETE: (companyId: number, bankTransferId: number): string =>
      `/api/company/${companyId}/bank-transfer/${bankTransferId}`,
  },

  companyStatisticsController: {
    getRegistrationStats: (companyId: number): string =>
      `/api/company/${companyId}/statistics/registrations`,
    getVisitorStats: (companyId: number): string =>
      `/api/company/${companyId}/statistics/visitors`,
    getOrderStats: (companyId: number): string =>
      `/api/company/${companyId}/statistics/orders`,
    getPaymentStats: (companyId: number): string =>
      `/api/company/${companyId}/statistics/payments`,
  },

  companyShopController: {
    GET: {
      shopHome: (companyId: number): string => `/api/company/${companyId}/shop`,
      banner: (companyId: number): string =>
        `/api/company/${companyId}/shop/banner`,
      shopProducts: (companyId: number): string =>
        `/api/company/${companyId}/shop/product`,
      section: (companyId: number): string =>
        `/api/company/${companyId}/shop/section`,
    },
    POST: {
      banner: (companyId: number): string =>
        `/api/company/${companyId}/shop/banner`,
      section: (companyId: number): string =>
        `/api/company/${companyId}/shop/section`,
    },
    // setting 은 전체 치환이고 header/footer 는 부분 수정이다. 절대 섞지 말 것.
    PATCH: {
      setting: (companyId: number): string =>
        `/api/company/${companyId}/shop/setting`,
      header: (companyId: number): string =>
        `/api/company/${companyId}/shop/header`,
      footer: (companyId: number): string =>
        `/api/company/${companyId}/shop/footer`,
    },
    PUT: {
      bestSellers: (companyId: number): string =>
        `/api/company/${companyId}/shop/bestseller`,
      banner: (companyId: number, bannerId: number): string =>
        `/api/company/${companyId}/shop/banner/${bannerId}`,
      stripBanner: (companyId: number): string =>
        `/api/company/${companyId}/shop/strip-banner`,
      section: (companyId: number, sectionId: number): string =>
        `/api/company/${companyId}/shop/section/${sectionId}`,
      sectionOrder: (companyId: number): string =>
        `/api/company/${companyId}/shop/section/order`,
    },
    DELETE: {
      banner: (companyId: number, bannerId: number): string =>
        `/api/company/${companyId}/shop/banner/${bannerId}`,
      stripBanner: (companyId: number): string =>
        `/api/company/${companyId}/shop/strip-banner`,
      section: (companyId: number, sectionId: number): string =>
        `/api/company/${companyId}/shop/section/${sectionId}`,
    },
  },

  companyJobController: {
    GET: (companyId: number, machineId: number): string =>
      `/api/company/${companyId}/machine/${machineId}/job`,
  },

  companyKpiController: {
    GET: {
      statistics: "/api/company/kpi/statistics" as const,
      statisticsPeriod: "/api/company/kpi/statistics/period" as const,
      productionLogs: `/api/company/kpi/statistics/production-logs`,
      config: "/api/company/kpi/config" as const,
      statisticsSlots: "/api/company/kpi/statistics/slots" as const,
      machines: "/api/company/kpi/machines" as const,
      // KPI 2.6 카드 대시보드
      cards: "/api/company/kpi/cards" as const,
    },
    POST: {
      slot: "/api/company/kpi/slot" as const,
      // KPI 2.6 동적 계산(미리보기) + 카드 생성
      compute: "/api/company/kpi/compute" as const,
      card: "/api/company/kpi/cards" as const,
    },
    PUT: {
      slot: (id: number): string => `/api/company/kpi/slot/${id}`,
      card: (id: number): string => `/api/company/kpi/cards/${id}`,
      cardsOrder: "/api/company/kpi/cards/order" as const,
    },
    DELETE: {
      slot: (id: number): string => `/api/company/kpi/slot/${id}`,
      card: (id: number): string => `/api/company/kpi/cards/${id}`,
    },
  },

  companyProductionRecordController: {
    GET: (companyId: number): string =>
      `/api/company/${companyId}/production-records`,
  },

  adminMachineController: {
    GET: (companyId: number): string =>
      `/api/admin/company/${companyId}/machine`,
  },

  /** 업체에 설치된 장비(대수) 관리 — 등록·조회·별칭·노출·삭제. */
  adminInstalledMachineController: {
    REGISTER: "/api/admin/machine" as const,
    GET_BY_ROUTER: (routerId: number): string =>
      `/api/admin/machine/router/${routerId}`,
    UPDATE_ALIAS: (machineId: number): string =>
      `/api/admin/machine/${machineId}/alias`,
    UPDATE_VISIBILITY: (machineId: number): string =>
      `/api/admin/machine/${machineId}/visibility`,
    DELETE: (machineId: number): string => `/api/admin/machine/${machineId}`,
  },

  adminMachineDetailsByRouter: {
    GET: (companyId: number): string =>
      `/api/admin/company/${companyId}/machine/by-router`,
  },

  companyDepartmentController: {
    GET: (companyId: number): string => `/api/company/${companyId}/departments`,
    POST: (companyId: number): string =>
      `/api/company/${companyId}/departments`,
    PUT: (companyId: number, departmentId: number): string =>
      `/api/company/${companyId}/departments/${departmentId}`,
    PUT_REORDER: (companyId: number): string =>
      `/api/company/${companyId}/departments/reorder`,
    DELETE: (companyId: number, departmentId: number): string =>
      `/api/company/${companyId}/departments/${departmentId}`,
    POST_MEMBERS: (companyId: number, departmentId: number): string =>
      `/api/company/${companyId}/departments/${departmentId}/members`,
    DELETE_MEMBER: (
      companyId: number,
      departmentId: number,
      userId: number,
    ): string =>
      `/api/company/${companyId}/departments/${departmentId}/members/${userId}`,
  },

  companyShippingController: {
    POST: {
      bulkRegister: (companyId: number) =>
        `/api/company/${companyId}/shipping/register/bulk`,
    },
    GET: {
      template: (companyId: number) =>
        `/api/company/${companyId}/shipping/register/template`,
    },
  },

  companyMaintenanceController: {
    GET_STATS: (companyId: number): string =>
      `/api/company/${companyId}/maintenance/stats`,
    GET: (
      companyId: number,
      machineId?: number,
      type?: string,
      page?: number,
      size?: number,
    ): string => {
      const params = new URLSearchParams();
      if (machineId) params.append("machineId", machineId.toString());
      if (type) params.append("type", type);
      if (page !== undefined) params.append("page", page.toString());
      if (size !== undefined) params.append("size", size.toString());
      const query = params.toString();
      return `/api/company/${companyId}/maintenance${query ? `?${query}` : ""}`;
    },
    POST: (companyId: number): string =>
      `/api/company/${companyId}/maintenance`,
    PUT: (companyId: number, maintenanceId: number): string =>
      `/api/company/${companyId}/maintenance/${maintenanceId}`,
    DELETE: (companyId: number, maintenanceId: number): string =>
      `/api/company/${companyId}/maintenance/${maintenanceId}`,
  },

  companyBusinessClientController: {
    GET: {
      list: (companyId: number): string =>
        `/api/company/${companyId}/business-clients`,
      detail: (companyId: number, userId: number): string =>
        `/api/company/${companyId}/business-clients/${userId}`,
      orders: (companyId: number, userId: number): string =>
        `/api/company/${companyId}/business-clients/${userId}/orders`,
      orderFiles: (companyId: number, userId: number): string =>
        `/api/company/${companyId}/business-clients/${userId}/orders/files`,
    },
  },

  companyManagedClientController: {
    GET: {
      list: (companyId: number): string =>
        `/api/company/${companyId}/managed-clients`,
      detail: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}`,
      compare: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}/compare`,
      orders: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}/orders`,
      orderFiles: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}/orders/files`,
    },
    POST: {
      create: (companyId: number): string =>
        `/api/company/${companyId}/managed-clients`,
      uploadImage: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}/images`,
    },
    PUT: {
      update: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}`,
      linkUser: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}/link-user`,
      unlinkUser: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}/unlink-user`,
    },
    DELETE: {
      delete: (companyId: number, id: number): string =>
        `/api/company/${companyId}/managed-clients/${id}`,
      deleteImage: (companyId: number, id: number, imageId: number): string =>
        `/api/company/${companyId}/managed-clients/${id}/images/${imageId}`,
    },
  },

  companyOrderController: {
    GET: {
      list: (
        companyId: number,
        status?: string,
        keyword?: string,
        page?: number,
        size?: number,
        dateFrom?: string,
        dateTo?: string,
      ): string => {
        const params = new URLSearchParams();
        if (status && status !== "all") params.append("status", status);
        if (keyword) params.append("keyword", keyword);
        if (page !== undefined) params.append("page", page.toString());
        if (size !== undefined) params.append("size", size.toString());
        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);
        const query = params.toString();
        return `/api/company/${companyId}/orders${query ? `?${query}` : ""}`;
      },
      detail: (companyId: number, orderId: number): string =>
        `/api/company/${companyId}/orders/${orderId}`,
      cart: (companyId: number, userId: number): string =>
        `/api/company/${companyId}/users/${userId}/cart`,
      history: (companyId: number): string =>
        `/api/company/${companyId}/orders/history`,
      resubmissionList: (companyId: number): string => {
        return `/api/company/${companyId}/orders/resubmission`;
      },
    },
    POST: {
      create: (companyId: number): string => `/api/company/${companyId}/orders`,
      resubmission: (companyId: number, orderId: number, memo: string) => ({
        url: `/api/company/${companyId}/orders/${orderId}/resubmission`,
        body: { message: memo },
      }),
      updateTrackingInfo: (
        companyId: number,
        orderId: number,
        courier: string,
        trackingNumber: string,
      ) => ({
        url: `/api/company/${companyId}/orders/tracking/insert`,
        body: {
          orderId: orderId,
          courier: courier,
          trackingNumber: trackingNumber,
        },
      }),
      /** 결제 완료 처리 (BEFORE_PAYMENT → RECEIVED) */
      confirmPayment: (companyId: number, orderId: number, paymentMethod: string) => ({
        url: `/api/company/${companyId}/orders/${orderId}/confirm-payment`,
        body: { paymentMethod },
      }),
      trackingOrder: (
        companyId: number,
        orderId: number,
        trackingNumber: string,
        courier: string,
      ) => ({
        url: `/api/company/${companyId}/orders/tracking`,
        body: {
          orderId: orderId,
          trackingNumber: trackingNumber,
          courier: courier,
        },
      }),
      /**
       * 배송추적 수동 새로고침 — 택배사 API 를 직접 호출한다.
       *
       * 위 trackingOrder 는 저장된 이력을 읽을 뿐이고, 실제 조회는 이쪽이다.
       * 택배사 조회 한도 때문에 송장당 하루 사용 횟수가 제한되며,
       * 남은 횟수는 응답의 remainingManualRefresh 로 내려온다.
       */
      refreshTracking: (companyId: number, orderId: number) => ({
        url: `/api/company/${companyId}/shipping/${orderId}/tracking/refresh`,
        body: {},
      }),
    },
    PATCH: {
      acceptOrder: (companyId: number, orderId: number) => ({
        url: `/api/company/${companyId}/orders/${orderId}/status`,
        body: { nextStatus: "RECEIVED" },
      }),
    },
    PUT: {
      updateStatus: (companyId: number, orderId: number): string =>
        `/api/company/${companyId}/orders/${orderId}/status`,
    },
  },

  companyMaterialController: {
    GET: {
      list: (companyId: number) => {
        return `/api/company/${companyId}/materials`;
      },
    },
    POST: {
      createMaterial: (companyId: number, materialName: string) => ({
        url: `/api/company/${companyId}/materials`,
        body: { name: materialName, type: "SHEET" },
      }),
    },
    PUT: {
      updateMaterial: (
        companyId: number,
        id: number,
        materialName: string,
      ) => ({
        url: `/api/company/${companyId}/materials/${id}`,
        body: { name: materialName, type: "SHEET" },
      }),
    },
    DELETE: {
      deleteMaterial: (companyId: number, id: number) => {
        return `/api/company/${companyId}/materials/${id}`;
      },
    },
  },

  companyMaterialDetailController: {
    GET: {
      detail: (companyId: number, materialId: number) => {
        return `/api/company/${companyId}/materials/${materialId}`;
      },
    },
    POST: {
      createDetail: (
        companyId: number,
        materialId: number,
        data: {
          width?: number;
          height?: number;
          gram?: number;
          stockCount?: number;
          sheetSize?: string;
          sheetType?: string;
        },
      ) => ({
        url: `/api/company/${companyId}/materials/${materialId}/details`,
        body: data,
      }),
    },
    PUT: {
      updateDetail: (
        companyId: number,
        materialId: number,
        detailId: number,
        data: {
          width?: number;
          height?: number;
          gram?: number;
          stockCount?: number;
          sheetSize?: string;
          sheetType?: string;
        },
      ) => ({
        url: `/api/company/${companyId}/materials/${materialId}/details/${detailId}`,
        body: data,
      }),
    },
    DELETE: {
      deleteDetail: (
        companyId: number,
        materialId: number,
        detailId: number,
      ) => {
        return `/api/company/${companyId}/materials/${materialId}/details/${detailId}`;
      },
    },
  },

  // ===== 신규 상품 시스템 API =====

  // 상품 설정 조회
  companyProductConfigController: {
    GET: (companyId: number): string =>
      `/api/company/${companyId}/product-config`,
  },

  // 신규 상품 CRUD (/products 복수형)
  companyProductsController: {
    GET: (companyId: number): string => `/api/company/${companyId}/products`,
    GET_DETAIL: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}`,
    POST: (companyId: number): string => `/api/company/${companyId}/products`,
    PUT: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}`,
    DELETE: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}`,
    BULK_UPDATE_ORDER: (companyId: number): string =>
      `/api/company/${companyId}/products/order/bulk`,
    DUPLICATE: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/duplicate`,
  },

  // 상품 용지 (ProductPaper)
  companyProductPaperController: {
    GET: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/papers`,
    GET_DETAIL: (
      companyId: number,
      productId: number,
      paperId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/papers/${paperId}`,
    POST: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/papers`,
    PUT: (companyId: number, productId: number, paperId: number): string =>
      `/api/company/${companyId}/products/${productId}/papers/${paperId}`,
    DELETE: (companyId: number, productId: number, paperId: number): string =>
      `/api/company/${companyId}/products/${productId}/papers/${paperId}`,
  },

  // 사이즈 템플릿
  companySizeTemplateController: {
    GET: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/size-templates`,
    GET_DETAIL: (
      companyId: number,
      productId: number,
      sizeTemplateId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/size-templates/${sizeTemplateId}`,
    POST: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/size-templates`,
    PUT: (
      companyId: number,
      productId: number,
      sizeTemplateId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/size-templates/${sizeTemplateId}`,
    DELETE: (
      companyId: number,
      productId: number,
      sizeTemplateId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/size-templates/${sizeTemplateId}`,
  },

  // 상품별 인쇄 구성 (ProductInkConfig)
  companyProductInkConfigController: {
    GET: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/ink-configs`,
    POST: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/ink-configs`,
    DELETE: (
      companyId: number,
      productId: number,
      productInkConfigId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/ink-configs/${productInkConfigId}`,
  },

  // 상품별 옵션
  companyProductOptionController: {
    GET: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/options`,
    GET_DETAIL: (
      companyId: number,
      productId: number,
      optionId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}`,
    POST: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/options`,
    PUT: (companyId: number, productId: number, optionId: number): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}`,
    DELETE: (companyId: number, productId: number, optionId: number): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}`,
    POST_INSERT_PAPER: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/options/insert-paper`,
    POST_END_PAPER: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/options/end-paper`,
    POST_PAPER_ORIENTATION: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/options/paper-orientation`,
    /** 같은 부모 안의 순서 일괄 저장. 부모의 현재 자식 ID 전체를 새 순서로 보낸다 */
    PUT_ORDER: (
      companyId: number,
      productId: number,
      scope: OptionReorderScope,
    ): string => {
      const base = `/api/company/${companyId}/products/${productId}/options`;
      if (scope.level === "option") return `${base}/order`;
      if (scope.level === "attribute") return `${base}/${scope.optionId}/attributes/order`;
      return `${base}/${scope.optionId}/attributes/${scope.attributeId}/values/order`;
    },
  },

  // 옵션 속성
  companyProductOptionAttributeController: {
    GET: (companyId: number, productId: number, optionId: number): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes`,
    GET_DETAIL: (
      companyId: number,
      productId: number,
      optionId: number,
      attributeId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes/${attributeId}`,
    POST: (companyId: number, productId: number, optionId: number): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes`,
    PUT: (
      companyId: number,
      productId: number,
      optionId: number,
      attributeId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes/${attributeId}`,
    DELETE: (
      companyId: number,
      productId: number,
      optionId: number,
      attributeId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes/${attributeId}`,
  },

  // 옵션 속성값
  companyProductOptionAttributeValueController: {
    GET: (
      companyId: number,
      productId: number,
      optionId: number,
      attributeId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes/${attributeId}/values`,
    GET_DETAIL: (
      companyId: number,
      productId: number,
      optionId: number,
      attributeId: number,
      valueId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes/${attributeId}/values/${valueId}`,
    POST: (
      companyId: number,
      productId: number,
      optionId: number,
      attributeId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes/${attributeId}/values`,
    PUT: (
      companyId: number,
      productId: number,
      optionId: number,
      attributeId: number,
      valueId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes/${attributeId}/values/${valueId}`,
    DELETE: (
      companyId: number,
      productId: number,
      optionId: number,
      attributeId: number,
      valueId: number,
    ): string =>
      `/api/company/${companyId}/products/${productId}/options/${optionId}/attributes/${attributeId}/values/${valueId}`,
  },

  // 가격 계산
  companyPriceCalculationController: {
    POST: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/calculate-price`,
  },

  // 견적식 미리보기
  companyEstimatePreviewController: {
    POST: (companyId: number, productId: number): string =>
      `/api/company/${companyId}/products/${productId}/estimate-preview`,
  },

  // 종이 규격 (PaperSpec) - 회사 레벨
  companyPaperSpecController: {
    GET: (companyId: number): string => `/api/company/${companyId}/paper-spec`,
    GET_DETAIL: (companyId: number, paperSpecId: number): string =>
      `/api/company/${companyId}/paper-spec/${paperSpecId}`,
  },

  // 일반(낱장) 용지 사이즈 규격 (SheetSizeSpec) - 회사 레벨, 회사가 직접 CRUD 관리
  companySheetSizeSpecController: {
    GET: (companyId: number): string => `/api/company/${companyId}/sheet-size-specs`,
    GET_DETAIL: (companyId: number, sheetSizeSpecId: number): string =>
      `/api/company/${companyId}/sheet-size-specs/${sheetSizeSpecId}`,
    POST: (companyId: number): string => `/api/company/${companyId}/sheet-size-specs`,
    PUT: (companyId: number, sheetSizeSpecId: number): string =>
      `/api/company/${companyId}/sheet-size-specs/${sheetSizeSpecId}`,
    DELETE: (companyId: number, sheetSizeSpecId: number): string =>
      `/api/company/${companyId}/sheet-size-specs/${sheetSizeSpecId}`,
  },

  // 롤 용지 사이즈 규격 (RollSizeSpec) - 회사 레벨, 회사가 직접 CRUD 관리
  companyRollSizeSpecController: {
    GET: (companyId: number): string => `/api/company/${companyId}/roll-size-specs`,
    GET_DETAIL: (companyId: number, rollSizeSpecId: number): string =>
      `/api/company/${companyId}/roll-size-specs/${rollSizeSpecId}`,
    POST: (companyId: number): string => `/api/company/${companyId}/roll-size-specs`,
    PUT: (companyId: number, rollSizeSpecId: number): string =>
      `/api/company/${companyId}/roll-size-specs/${rollSizeSpecId}`,
    DELETE: (companyId: number, rollSizeSpecId: number): string =>
      `/api/company/${companyId}/roll-size-specs/${rollSizeSpecId}`,
  },

  // 자재 하위 리소스 (새 API)
  companyMaterialSheetPaperController: {
    GET: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/sheet-papers`,
    GET_DETAIL: (
      companyId: number,
      materialId: number,
      sheetPaperId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/sheet-papers/${sheetPaperId}`,
    POST: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/sheet-papers`,
    PUT: (
      companyId: number,
      materialId: number,
      sheetPaperId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/sheet-papers/${sheetPaperId}`,
    DELETE: (
      companyId: number,
      materialId: number,
      sheetPaperId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/sheet-papers/${sheetPaperId}`,
  },

  companyMaterialRollPaperController: {
    GET: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/roll-papers`,
    GET_DETAIL: (
      companyId: number,
      materialId: number,
      rollPaperId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/roll-papers/${rollPaperId}`,
    POST: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/roll-papers`,
    PUT: (companyId: number, materialId: number, rollPaperId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/roll-papers/${rollPaperId}`,
    DELETE: (
      companyId: number,
      materialId: number,
      rollPaperId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/roll-papers/${rollPaperId}`,
  },

  companyMaterialInkController: {
    GET: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inks`,
    GET_DETAIL: (
      companyId: number,
      materialId: number,
      inkId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/inks/${inkId}`,
    POST: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inks`,
    PUT: (companyId: number, materialId: number, inkId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inks/${inkId}`,
    DELETE: (companyId: number, materialId: number, inkId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inks/${inkId}`,
  },

  companyMaterialInkConfigController: {
    GET: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/ink-configs`,
    GET_DETAIL: (
      companyId: number,
      materialId: number,
      inkConfigId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/ink-configs/${inkConfigId}`,
    POST: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/ink-configs`,
    PUT: (companyId: number, materialId: number, inkConfigId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/ink-configs/${inkConfigId}`,
    DELETE: (
      companyId: number,
      materialId: number,
      inkConfigId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/ink-configs/${inkConfigId}`,
  },

  companyMaterialOtherController: {
    GET: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/others`,
    GET_DETAIL: (
      companyId: number,
      materialId: number,
      otherId: number,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/others/${otherId}`,
    POST: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/others`,
    PUT: (companyId: number, materialId: number, otherId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/others/${otherId}`,
    DELETE: (companyId: number, materialId: number, otherId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/others/${otherId}`,
  },

  // 자재 CRUD (새 API - type 필터 지원)
  companyMaterialsController: {
    GET: (companyId: number, type?: string): string => {
      const params = type ? `?type=${type}` : "";
      return `/api/company/${companyId}/materials${params}`;
    },
    GET_DETAIL: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}`,
    POST: (companyId: number): string => `/api/company/${companyId}/materials`,
    PUT: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}`,
    DELETE: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}`,
  },

  // 재고 관리
  companyInventoryController: {
    GET_ALL: (companyId: number, type?: string): string => {
      const params = type ? `?type=${type}` : "";
      return `/api/company/${companyId}/inventory${params}`;
    },
    GET: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inventory`,
    GET_LOGS: (
      companyId: number,
      materialId: number,
      page = 0,
      size = 20,
    ): string =>
      `/api/company/${companyId}/materials/${materialId}/inventory/logs?page=${page}&size=${size}`,
    POST_RECEIVE: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inventory/receive`,
    POST_DEDUCT: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inventory/deduct`,
    POST_RETURN: (companyId: number, materialId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inventory/return`,
    PUT_LOG: (companyId: number, materialId: number, logId: number): string =>
      `/api/company/${companyId}/materials/${materialId}/inventory/logs/${logId}`,
  },

  companyOnboardingController: {
    GET: (companyId: number): string => `/api/company/${companyId}/onboarding`,
  },

  companyInvoiceController: {
    GET: (companyId: number): string =>
      `/api/company/${companyId}/invoices`,
    PUT_STATUS: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invoices/${id}/status`,
    PUT_BATCH_ISSUE: (companyId: number): string =>
      `/api/company/${companyId}/invoices/batch-issue`,
    POST: (companyId: number): string => `/api/company/${companyId}/invoices`,
    GET_ONE: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invoices/${id}`,
    PUT: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invoices/${id}`,
    POST_ISSUE: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invoices/${id}/issue`,
    POST_CANCEL: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invoices/${id}/cancel`,
    POST_REFRESH: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invoices/${id}/refresh`,
    GET_VIEW_URL: (companyId: number, id: number): string =>
      `/api/company/${companyId}/invoices/${id}/view-url`,
    GET_CONNECTION: (companyId: number): string =>
      `/api/company/${companyId}/invoices/connection`,
    POST_CERTIFICATE_URL: (companyId: number): string =>
      `/api/company/${companyId}/invoices/certificate/regist-url`,
    /** 원본 증빙에 딸린 환불 조정 건(취소/부분취소/수정세금계산서) */
    GET_ADJUSTMENTS: (companyId: number, invoiceId: number): string =>
      `/api/company/${companyId}/invoices/${invoiceId}/adjustments`,
    POST_ADJUSTMENT_EXECUTE: (companyId: number, adjustmentId: number): string =>
      `/api/company/${companyId}/invoices/adjustments/${adjustmentId}/execute`,
    POST_ADJUSTMENT_DISCARD: (companyId: number, adjustmentId: number): string =>
      `/api/company/${companyId}/invoices/adjustments/${adjustmentId}/discard`,
    GET_ADJUSTMENT_SUMMARY: (companyId: number): string =>
      `/api/company/${companyId}/invoices/adjustments/summary`,
  },

  /** 주문 부분 환불 기록 (무통장입금 주문만. 실제 송금은 시스템 밖에서 한다) */
  companyOrderRefundController: {
    GET: (companyId: number, orderId: number): string =>
      `/api/company/${companyId}/orders/${orderId}/refunds`,
    POST: (companyId: number, orderId: number): string =>
      `/api/company/${companyId}/orders/${orderId}/refunds`,
  },

  shopController: {
    GET: {
      shopHome: "/api/shop" as const,
      categories: (shopId: number): string => `/api/shop/${shopId}/category`,
      recentlyViewedProducts: (shopId: number): string =>
        `/api/shop/${shopId}/recentlyViewedProducts`,
    },
  },

  shopProductController: {
    GET: {
      bestsellerList: (shopId: number): string =>
        `/api/shop/${shopId}/bestseller`,
      recentlyViewedProducts: (shopId: number): string =>
        `/api/shop/${shopId}/recentlyViewedProducts`,
      productList: (shopId: number): string => `/api/shop/${shopId}/product`,
    },
  },

  shopBankTransferController: {
    GET: (shopId: number): string => `/api/shop/${shopId}/bank-transfer`,
  },

  contentController: {
    notices: {
      GET: {
        list: (companyId: number): string =>
          `/api/company/${companyId}/contents/notices`,
        detail: (companyId: number, noticeId: number): string =>
          `/api/company/${companyId}/contents/notices/${noticeId}`,
      },
      POST: (companyId: number): string =>
        `/api/company/${companyId}/contents/notices`,
      PUT: (companyId: number, noticeId: number): string =>
        `/api/company/${companyId}/contents/notices/${noticeId}`,
      DELETE: (companyId: number, noticeId: number): string =>
        `/api/company/${companyId}/contents/notices/${noticeId}`,
    },
    faqs: {
      GET: {
        list: (companyId: number, category?: string): string => {
          const params = category ? `?category=${category}` : "";
          return `/api/company/${companyId}/contents/faqs${params}`;
        },
        detail: (companyId: number, faqId: number): string =>
          `/api/company/${companyId}/contents/faqs/${faqId}`,
      },
      POST: (companyId: number): string =>
        `/api/company/${companyId}/contents/faqs`,
      PUT: (companyId: number, faqId: number): string =>
        `/api/company/${companyId}/contents/faqs/${faqId}`,
      PATCH: (companyId: number, faqId: number): string =>
        `/api/company/${companyId}/contents/faqs/${faqId}/order`,
      DELETE: (companyId: number, faqId: number): string =>
        `/api/company/${companyId}/contents/faqs/${faqId}`,
    },
    popups: {
      GET: {
        list: (companyId: number): string =>
          `/api/company/${companyId}/contents/popups`,
      },
      POST: (companyId: number): string =>
        `/api/company/${companyId}/contents/popups`,
      PUT: {
        update: (companyId: number, popupId: number): string =>
          `/api/company/${companyId}/contents/popups/${popupId}`,
        reorder: (companyId: number): string =>
          `/api/company/${companyId}/contents/popups/order`,
      },
      DELETE: (companyId: number, popupId: number): string =>
        `/api/company/${companyId}/contents/popups/${popupId}`,
    },
    portfolios: {
      GET: {
        list: (companyId: number): string =>
          `/api/company/${companyId}/contents/portfolios`,
        detail: (companyId: number, portfolioId: number): string =>
          `/api/company/${companyId}/contents/portfolios/${portfolioId}`,
        section: (companyId: number): string =>
          `/api/company/${companyId}/contents/portfolio/settings`,
      },
      POST: (companyId: number): string =>
        `/api/company/${companyId}/contents/portfolios`,
      PUT: {
        update: (companyId: number, portfolioId: number): string =>
          `/api/company/${companyId}/contents/portfolios/${portfolioId}`,
        reorder: (companyId: number): string =>
          `/api/company/${companyId}/contents/portfolios/order`,
        section: (companyId: number): string =>
          `/api/company/${companyId}/contents/portfolio/settings`,
        /*
          버튼/연결 주문은 기존 수정 API(PUT .../portfolios/{id})에 섞지 않는다.
          그쪽은 multipart 라 JSON 배열을 싣기 번거롭고 이미지 교체와 관심사가 다르다.
          둘 다 "전체 치환" 시맨틱이다.
        */
        buttons: (companyId: number, portfolioId: number): string =>
          `/api/company/${companyId}/contents/portfolios/${portfolioId}/buttons`,
        orders: (companyId: number, portfolioId: number): string =>
          `/api/company/${companyId}/contents/portfolios/${portfolioId}/orders`,
      },
      DELETE: (companyId: number, portfolioId: number): string =>
        `/api/company/${companyId}/contents/portfolios/${portfolioId}`,
    },
    fonts: {
      GET: {
        list: (companyId: number): string =>
          `/api/company/${companyId}/contents/fonts`,
      },
      POST: (companyId: number): string =>
        `/api/company/${companyId}/contents/fonts`,
      PUT: (companyId: number, fontId: number): string =>
        `/api/company/${companyId}/contents/fonts/${fontId}`,
      DELETE: (companyId: number, fontId: number): string =>
        `/api/company/${companyId}/contents/fonts/${fontId}`,
    },
  },

  // 회사 이메일 설정 API (발신 자격증명 + IMAP 수신 연동 통합)
  // 기존 /mail-integration 엔드포인트는 제거되고 /email-settings 로 통합되었다.
  mailIntegrationController: {
    GET: (companyId: number): string =>
      `/api/company/${companyId}/email-settings`,
    PUT: (companyId: number): string =>
      `/api/company/${companyId}/email-settings`,
  },

  // 추가 서비스 연동 플러그인 API (내 회사 정보 > 추가 서비스 연동)
  companyPluginController: {
    LIST: (companyId: number): string => `/api/company/${companyId}/plugins`,
    PUT: (companyId: number, pluginType: string): string =>
      `/api/company/${companyId}/plugins/${pluginType}`,
    DELETE: (companyId: number, pluginType: string): string =>
      `/api/company/${companyId}/plugins/${pluginType}`,
  },

  // 주문 메일함 API
  orderMailboxController: {
    GET: {
      list: (
        companyId: number,
        status?: string,
        keyword?: string,
        page?: number,
        size?: number,
      ): string => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (keyword) params.append("keyword", keyword);
        if (page !== undefined) params.append("page", String(page));
        if (size !== undefined) params.append("size", String(size));
        const query = params.toString();
        return `/api/company/${companyId}/order-mailbox${query ? `?${query}` : ""}`;
      },
      detail: (companyId: number, id: number): string =>
        `/api/company/${companyId}/order-mailbox/${id}`,
    },
    POST: {
      refresh: (companyId: number): string =>
        `/api/company/${companyId}/order-mailbox/refresh`,
      createOrder: (companyId: number, id: number): string =>
        `/api/company/${companyId}/order-mailbox/${id}/create-order`,
      matchCustomer: (companyId: number, id: number): string =>
        `/api/company/${companyId}/order-mailbox/${id}/match-customer`,
      downloadEstimate: (companyId: number, id: number): string =>
        `/api/company/${companyId}/order-mailbox/${id}/estimate/download`,
      sendEstimate: (companyId: number, id: number): string =>
        `/api/company/${companyId}/order-mailbox/${id}/estimate/send`,
    },
  },

  // 주문 인식 규칙 API
  orderRecognitionRuleController: {
    GET: {
      rule: (companyId: number): string =>
        `/api/company/${companyId}/order-recognition-rule`,
      customerEmails: (companyId: number): string =>
        `/api/company/${companyId}/order-recognition-rule/customer-emails`,
      suggestCustomerEmails: (companyId: number, email: string): string =>
        `/api/company/${companyId}/order-recognition-rule/customer-emails/suggest?email=${encodeURIComponent(email)}`,
    },
    PUT: (companyId: number): string =>
      `/api/company/${companyId}/order-recognition-rule`,
    POST: (companyId: number): string =>
      `/api/company/${companyId}/order-recognition-rule/customer-emails`,
    DELETE: (companyId: number, id: number): string =>
      `/api/company/${companyId}/order-recognition-rule/customer-emails/${id}`,
  },

  // KPI 관리 API
  // Todo Template 관리 API (작업 공정 관리)
  todoTemplateController: {
    GET: {
      list: (companyId: number, productId: number): string =>
        `/api/company/${companyId}/products/${productId}/todo-templates`,
      detail: (
        companyId: number,
        productId: number,
        templateId: number,
      ): string =>
        `/api/company/${companyId}/products/${productId}/todo-templates/${templateId}`,
    },
    POST: {
      create: (companyId: number, productId: number): string =>
        `/api/company/${companyId}/products/${productId}/todo-templates`,
    },
    PUT: {
      update: (
        companyId: number,
        productId: number,
        templateId: number,
      ): string =>
        `/api/company/${companyId}/products/${productId}/todo-templates/${templateId}`,
      reorder: (companyId: number, productId: number): string =>
        `/api/company/${companyId}/products/${productId}/todo-templates/reorder`,
    },
    PATCH: {
      toggleActive: (
        companyId: number,
        productId: number,
        templateId: number,
      ): string =>
        `/api/company/${companyId}/products/${productId}/todo-templates/${templateId}/toggle-active`,
    },
    DELETE: {
      delete: (
        companyId: number,
        productId: number,
        templateId: number,
      ): string =>
        `/api/company/${companyId}/products/${productId}/todo-templates/${templateId}`,
    },
  },

  // Order Todo 관리 API (작업 현황 관리)
  orderTodoController: {
    GET: {
      list: (companyId: number, orderId: number): string =>
        `/api/company/${companyId}/orders/${orderId}/todos`,
      detail: (companyId: number, orderId: number, todoId: number): string =>
        `/api/company/${companyId}/orders/${orderId}/todos/${todoId}`,
      companyTodos: (companyId: number): string =>
        `/api/company/${companyId}/todos`,
      myTodos: (companyId: number): string =>
        `/api/company/${companyId}/my-todos`,
    },
    PATCH: {
      updateStatus: (
        companyId: number,
        orderId: number,
        todoId: number,
      ): string =>
        `/api/company/${companyId}/orders/${orderId}/todos/${todoId}/status`,
    },
    PUT: {
      updateAssignees: (
        companyId: number,
        orderId: number,
        todoId: number,
      ): string =>
        `/api/company/${companyId}/orders/${orderId}/todos/${todoId}/assignees`,
    },
    POST: {
      addNote: (companyId: number, orderId: number, todoId: number): string =>
        `/api/company/${companyId}/orders/${orderId}/todos/${todoId}/notes`,
    },
  },

  // 가격 정보 관리 API (손지율, 배송비, 이벤트 할인)
  companyPricingController: {
    // 손지율 비용 관리
    wasteRate: {
      GET: (companyId: number): string =>
        `/api/company/${companyId}/pricing/waste-rate`,
      POST: (companyId: number): string =>
        `/api/company/${companyId}/pricing/waste-rate`,
      PUT: (companyId: number, wasteRateId: number): string =>
        `/api/company/${companyId}/pricing/waste-rate/${wasteRateId}`,
      DELETE: (companyId: number, wasteRateId: number): string =>
        `/api/company/${companyId}/pricing/waste-rate/${wasteRateId}`,
    },
    // 배송비 관리
    shippingFee: {
      GET: (companyId: number): string =>
        `/api/company/${companyId}/pricing/shipping-fee`,
      PUT: (companyId: number): string =>
        `/api/company/${companyId}/pricing/shipping-fee`,
    },
    // 이벤트 할인 관리
    eventDiscount: {
      GET: (companyId: number): string =>
        `/api/company/${companyId}/pricing/event-discounts`,
      POST: (companyId: number): string =>
        `/api/company/${companyId}/pricing/event-discounts`,
      PUT: (companyId: number, eventDiscountId: number): string =>
        `/api/company/${companyId}/pricing/event-discounts/${eventDiscountId}`,
      DELETE: (companyId: number, eventDiscountId: number): string =>
        `/api/company/${companyId}/pricing/event-discounts/${eventDiscountId}`,
    },
  },

  // KPI 2.3 어드민 API (/api/admin/kpi, ADMIN)
  //  - 가입(플러그인) 관리 + 전역설정: AdminKpiPluginController
  //  - 슬롯/보고/수동/compute: AdminKpiController (키 = 스마트 공방 pluginId)
  adminKpiController: {
    // ① KPI 전역 설정 (plugin_sbiz24_detail.kpi_*, SMART_WORKSHOP 단위 — pluginId 로 조회/수정)
    GET_SBIZ24_SETTINGS: (pluginId: number): string =>
      `/api/admin/kpi/settings/plugin/${pluginId}`,
    PATCH_SBIZ24_SETTINGS: (pluginId: number): string =>
      `/api/admin/kpi/settings/plugin/${pluginId}`,

    // ② 가입 사업 = plugins CRUD
    GET_PLUGINS_BY_COMPANY: (companyId: number): string =>
      `/api/admin/kpi/plugin/company/${companyId}`,
    POST_PLUGIN: "/api/admin/kpi/plugin" as const,
    PUT_PLUGIN: (pluginId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}`,
    DELETE_PLUGIN: (pluginId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}`,
    // 대표장비(LEVEL1 보고대상) 지정 — 슬롯 측정 장비 중 하나여야 함
    PUT_REPRESENTATIVE: (pluginId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}/representative`,

    // ③ 슬롯 = sbiz24_config CRUD (키 = pluginId)
    GET_SLOTS: (pluginId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}/slot`,
    POST_SLOT: (pluginId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}/slot`,
    // pluginId 필수 — sbiz24_config/factory_config 는 별개 테이블이라 slotId 만으로는 겹칠 수 있음.
    PUT_SLOT: (pluginId: number, slotId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}/slot/${slotId}`,
    DELETE_SLOT: (pluginId: number, slotId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}/slot/${slotId}`,

    // ④ 보고 현황 (요약/캘린더/로그, 키 = pluginId)
    GET_SUMMARY: (pluginId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}/summary`,
    GET_CALENDAR: (
      pluginId: number,
      year: number,
      month: number,
      slotId?: number,
    ): string => {
      const params = new URLSearchParams();
      params.append("year", year.toString());
      params.append("month", month.toString());
      if (slotId !== undefined) params.append("slotId", slotId.toString());
      return `/api/admin/kpi/plugin/${pluginId}/calendar?${params.toString()}`;
    },
    GET_LOGS: (pluginId: number, page?: number, size?: number): string => {
      const params = new URLSearchParams();
      if (page !== undefined) params.append("page", page.toString());
      if (size !== undefined) params.append("size", size.toString());
      const query = params.toString();
      return `/api/admin/kpi/plugin/${pluginId}/logs${query ? `?${query}` : ""}`;
    },

    // ⑤ 스마트공장 로그 이력 (키 = pluginId)
    GET_FACTORY_LOGS: (pluginId: number, page?: number, size?: number): string => {
      const params = new URLSearchParams();
      if (page !== undefined) params.append("page", page.toString());
      if (size !== undefined) params.append("size", size.toString());
      const query = params.toString();
      return `/api/admin/kpi/plugin/${pluginId}/factory-logs${query ? `?${query}` : ""}`;
    },

    // ⑥ 수동 보고 (키 = pluginId)
    POST_MANUAL_REPORT: (pluginId: number): string =>
      `/api/admin/kpi/plugin/${pluginId}/manual-report`,

    // ⑦ 동적 계산 (어드민 그래프 — 특정 회사 KPI 시계열)
    POST_COMPUTE: (companyId: number): string =>
      `/api/admin/kpi/compute?companyId=${companyId}`,
  },
};

export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: "/api/auth/login",
    SIGNUP: "/api/auth/signup",
    SOCIAL: "/api/auth/social",
    BUSINESS_SIGNUP: "/api/auth/business/signup",
    VERIFY_PASSWORD: "/api/auth/verifyPassword",
    REISSUE: "/api/auth/reissue",
  },

  // User endpoints
  USER: {
    BASE: "/api/user",
    PROFILE: "/api/user",
    UPDATE_PROFILE: "/api/user",
    DELETE_ACCOUNT: "/api/user",
    CHANGE_PASSWORD: "/api/user/password",
    REQUEST_COMPANY: "/api/company/request",
    CANCEL_REQUEST: "/api/user/cancelRequest",
  },

  // Company endpoints
  COMPANY: {
    CREATE: "/api/company",
    GET_INFO: "/api/company/info",
    UPDATE_INFO: "/api/company/info",
    UPDATE_USER_ROLE: "/api/company/user/role",
    GET_USERS: "/api/company/user",
    SEARCH_USERS: "/api/company/user/search",
    APPROVE_COMPANY: "/api/company/approveCompany",
    APPROVED_COMPANY: "/api/company/approvedCompany",
    GET_ADMIN_INFO: "/api/company/info/adminInfo",
    ADDRESS_CALLBACK: "/api/company/address",
  },

  // Admin endpoints
  ADMIN: {
    // Member management
    MEMBERS: {
      LIST: (companyId: number) => `/api/admin/companies/${companyId}/users`,
      GET: (companyId: number, userId: number) =>
        `/api/admin/companies/${companyId}/users/${userId}`,
      CREATE: (companyId: number) => `/api/admin/companies/${companyId}/users`,
      UPDATE: (companyId: number, userId: number) =>
        `/api/admin/companies/${companyId}/users/${userId}`,
      DELETE: (companyId: number, userId: number) =>
        `/api/admin/companies/${companyId}/users/${userId}`,
    },
    // Company info management
    COMPANY_INFO: {
      GET: (companyId: number) =>
        `/api/admin/companies/${companyId}/company-info`,
      CREATE: (companyId: number) =>
        `/api/admin/companies/${companyId}/company-info`,
      UPDATE: (companyId: number) =>
        `/api/admin/companies/${companyId}/company-info`,
      DELETE: (companyId: number) =>
        `/api/admin/companies/${companyId}/company-info`,
    },
    // Banner management
    BANNERS: {
      LIST: (companyId: number) => `/api/admin/companies/${companyId}/banners`,
      GET: (companyId: number, bannerId: number) =>
        `/api/admin/companies/${companyId}/banners/${bannerId}`,
      CREATE: (companyId: number) =>
        `/api/admin/companies/${companyId}/banners`,
      UPDATE: (companyId: number, bannerId: number) =>
        `/api/admin/companies/${companyId}/banners/${bannerId}`,
      DELETE: (companyId: number, bannerId: number) =>
        `/api/admin/companies/${companyId}/banners/${bannerId}`,
    },
    // KPI management
    KPI: {
      LIST: (companyId: number, deviceId: number) =>
        `/api/admin/companies/${companyId}/devices/${deviceId}/kpis`,
      CREATE: (companyId: number, deviceId: number) =>
        `/api/admin/companies/${companyId}/devices/${deviceId}/kpis`,
      DELETE: (companyId: number, deviceId: number) =>
        `/api/admin/companies/${companyId}/devices/${deviceId}/kpis`,
    },
  },

  // Router endpoints
  ROUTER: {
    CREATE: "/api/router/create",
    GET_BY_API_KEY: "/api/router",
    GET_BY_COMPANY: (companyId: number) => `/api/company/${companyId}/router`,
    UPDATE: (id: number) => `/api/router/${id}/update`,
  },

  // Machine endpoints
  MACHINE: {
    GET: (machineId: number) => `/api/machine/${machineId}`,
    LIST: "/api/machine/list",
    CREATE: "/api/machine/create",
    UPDATE: (machineId: number) => `/api/machine/${machineId}/update`,
    DELETE: (machineId: number) => `/api/machine/${machineId}/delete`,
  },

  // Machine Job endpoints
  MACHINE_JOB: {
    GET_LIST: (machineId: number) => `/api/machines/${machineId}/jobs`,
    GET_STATISTICS: (machineId: number) =>
      `/api/machines/${machineId}/jobs/statistics`,
    GET_BY_MACHINE: "/api/machines/create",
  },

  // Machine Product endpoints
  MACHINE_PRODUCT: {
    CREATE: "/api/machine-product/create",
    SEARCH: "/api/machine-product",
  },
} as const;

export const API_CONFIG = {
  TIMEOUT: 30000,
} as const;

